// "This page is out of date" -- the one build message worth interrupting somebody for.
//
// The server has always reported its own commit, which cannot detect a browser holding the
// previous deploy's JavaScript; that was the blind spot `Cache-Control: no-cache` on /static
// mitigated rather than closed. The bundle carries the commit it was built from, so the two can
// be compared and a stale tab can say so.
//
// What GRO-80 changed is that the *healthy* comparison is now silent. Printing "build a83b995,
// deployed 2:05 PM" on every page load is what taught people to skip the line this needs them
// to read.
//
// What GRO-82 changed is that there is now something to compare locally, where both halves used
// to be empty and this rendered nothing at all. The local mismatch is a different sentence: the
// tab is not out of date, it is being answered by another working copy. Which comparison runs is
// lib/buildIdentity.ts.

import { compareBuild } from '../lib/buildIdentity';
import type { Build } from '../api/types';

export function StaleBuildNotice({ build }: { build: Build | undefined }) {
  const pageCommit = __BUILD_COMMIT__;
  const pageCheckout = __BUILD_CHECKOUT__;
  const match = compareBuild({ commit: pageCommit, checkout: pageCheckout }, build);

  if (match === 'match') return null;

  // Local: the numbers on the page are real and they belong to somebody else's branch, which
  // is worse than obviously-wrong data. Reloading cannot help -- the proxy target is decided
  // when the dev server starts -- so this says what will.
  if (match === 'other-checkout') {
    return (
      <div className="stalebar" role="status">
        <span>
          <b>Another checkout is answering this page.</b> It was built in{' '}
          <code>{pageCheckout}</code>, and the API replying is <code>{build?.checkout}</code>
          {build?.branch ? <> on <code>{build.branch}</code></> : null}. Restart the dev server
          with <code>API_PORT</code> set to this checkout's backend.
        </span>
      </div>
    );
  }

  return (
    <div className="stalebar" role="status">
      <span>
        <b>This page is out of date.</b> It is running <code>{pageCommit}</code> while the
        server is on <code>{build?.commit}</code>.
      </span>
      <button type="button" className="btn" onClick={() => window.location.reload()}>
        Reload
      </button>
    </div>
  );
}
